import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { useAudio } from '@/hooks/useAudio';
import {
  RollRequest,
  rollDice,
  evaluateResult,
  getStatModifier,
  STAT_NAMES,
  formatRollResult,
} from '@/utils/dice';

interface DiceRollModalProps {
  isOpen: boolean;
  rollRequest: RollRequest;
  onClose: () => void;
  onResult: (message: string) => void;
}

type RollPhase = 'ready' | 'rolling' | 'result';

export function DiceRollModal({ isOpen, rollRequest, onClose, onResult }: DiceRollModalProps) {
  const { hapticFeedback } = useTelegram();
  const { character } = useAuth();
  const { playDiceSound } = useAudio();

  const [phase, setPhase] = useState<RollPhase>('ready');
  const [rolls, setRolls] = useState<number[]>([]);
  const [animatingRolls, setAnimatingRolls] = useState<number[]>([]);

  const modifier = rollRequest.stat && character
    ? getStatModifier(character, rollRequest.stat)
    : 0;

  const statName = rollRequest.stat ? STAT_NAMES[rollRequest.stat] : null;

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase('ready');
      setRolls([]);
      setAnimatingRolls(Array(rollRequest.count).fill(1));
    }
  }, [isOpen, rollRequest.count]);

  const handleRoll = () => {
    setPhase('rolling');
    hapticFeedback('medium');
    playDiceSound(); // Play sound immediately on roll

    // Animate dice rolling
    let iterations = 0;
    const maxIterations = 15;

    const interval = setInterval(() => {
      setAnimatingRolls(rollDice(rollRequest.count, rollRequest.sides));
      iterations++;

      if (iterations >= maxIterations) {
        clearInterval(interval);
        const finalRolls = rollDice(rollRequest.count, rollRequest.sides);
        setRolls(finalRolls);
        setAnimatingRolls(finalRolls);
        setPhase('result');
        hapticFeedback('success');
      }
    }, 100);
  };

  const handleSendResult = () => {
    const message = formatRollResult(
      rollRequest.moveName,
      rolls,
      modifier,
      statName
    );
    onResult(message);
    onClose();
  };

  const total = rolls.reduce((a, b) => a + b, 0) + modifier;
  const result = evaluateResult(total);

  // Determine if this is a 2d6 move check (shows success/fail) or damage roll
  const isMoveCheck = rollRequest.count === 2 && rollRequest.sides === 6;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full border border-gray-700"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-1">
                {rollRequest.moveName}
              </h3>
              <p className="text-gray-400 text-sm">
                {rollRequest.dice}
                {rollRequest.stat && ` + ${STAT_NAMES[rollRequest.stat]}`}
              </p>
            </div>

            {/* Dice Display */}
            <div className="flex justify-center gap-4 mb-6">
              {(phase === 'rolling' ? animatingRolls : (rolls.length ? rolls : animatingRolls)).map((value, index) => (
                <motion.div
                  key={index}
                  animate={phase === 'rolling' ? {
                    rotate: [0, 360],
                    y: [0, -20, 0],
                  } : {}}
                  transition={{
                    duration: 0.3,
                    repeat: phase === 'rolling' ? Infinity : 0,
                  }}
                  className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold ${
                    phase === 'result'
                      ? 'bg-amber-500 text-black'
                      : 'bg-gray-700 text-white'
                  }`}
                >
                  {rollRequest.sides === 6 ? (
                    <DiceFace value={value} />
                  ) : (
                    <span>{value}</span>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Modifier Display */}
            {rollRequest.stat && character && (
              <div className="text-center mb-4">
                <span className="text-gray-400">
                  {statName}: {modifier >= 0 ? '+' : ''}{modifier}
                </span>
              </div>
            )}

            {/* Result Display */}
            {phase === 'result' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-6"
              >
                <p className="text-lg text-gray-300 mb-2">
                  {rolls.join(' + ')}
                  {modifier !== 0 && ` + ${modifier >= 0 ? '+' : ''}${modifier}`}
                  {' = '}
                  <span className="text-2xl font-bold text-white">{total}</span>
                </p>
                {isMoveCheck && (
                  <p className={`text-xl font-bold ${result.color}`}>
                    {result.text}
                  </p>
                )}
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {phase === 'ready' && (
                <button
                  onClick={handleRoll}
                  className="w-full py-3 rounded-xl bg-amber-500 text-black font-bold text-lg hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-2xl">🎲</span>
                  <span>Бросить!</span>
                </button>
              )}

              {phase === 'rolling' && (
                <div className="w-full py-3 rounded-xl bg-gray-700 text-gray-400 font-bold text-lg text-center">
                  Бросаем...
                </div>
              )}

              {phase === 'result' && (
                <button
                  onClick={handleSendResult}
                  className="w-full py-3 rounded-xl bg-green-600 text-white font-bold text-lg hover:bg-green-500 transition-colors"
                >
                  Отправить результат
                </button>
              )}

              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-xl bg-gray-700 text-gray-300 font-medium hover:bg-gray-600 transition-colors"
              >
                Отмена
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// D6 face with dots
function DiceFace({ value }: { value: number }) {
  const dotPositions: Record<number, string[]> = {
    1: ['center'],
    2: ['top-right', 'bottom-left'],
    3: ['top-right', 'center', 'bottom-left'],
    4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'],
    6: ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right'],
  };

  const positions = dotPositions[value] || [];

  const getPosition = (pos: string) => {
    switch (pos) {
      case 'top-left': return 'top-1 left-1';
      case 'top-right': return 'top-1 right-1';
      case 'center': return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
      case 'middle-left': return 'top-1/2 left-1 -translate-y-1/2';
      case 'middle-right': return 'top-1/2 right-1 -translate-y-1/2';
      case 'bottom-left': return 'bottom-1 left-1';
      case 'bottom-right': return 'bottom-1 right-1';
      default: return '';
    }
  };

  return (
    <div className="relative w-full h-full">
      {positions.map((pos, i) => (
        <div
          key={i}
          className={`absolute w-2.5 h-2.5 rounded-full bg-current ${getPosition(pos)}`}
        />
      ))}
    </div>
  );
}
